// src/pages/Earnings.js - Complete Earnings Management
import React, { useState, useEffect } from 'react';
import axios from 'axios';
import {
  BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, 
  ResponsiveContainer, LineChart, Line, Legend,
  PieChart, Pie, Cell
} from 'recharts';
import ExportButton from '../components/ExportButton';

function Earnings() {
  const [earnings, setEarnings] = useState({
    total: 0,
    commission: 0,
    thisMonth: 0,
    thisWeek: 0,
    today: 0,
    pendingPayouts: 0,
    totalWithdrawn: 0,
    lastPayout: null,
    averageDaily: 0,
  });
  const [transactions, setTransactions] = useState([]);
  const [weeklyTrend, setWeeklyTrend] = useState([]);
  const [dailyTrend, setDailyTrend] = useState([]);
  const [vehicleEarnings, setVehicleEarnings] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [filter, setFilter] = useState('all');
  const [timeRange, setTimeRange] = useState('week');

  const API_URL = 'https://boda-backend-wuea.onrender.com/api/admin';

  useEffect(() => {
    fetchEarnings();
  }, [timeRange]);

  const fetchEarnings = async () => {
    try {
      setLoading(true);
      setError(null);
      const token = localStorage.getItem('token');
      const headers = token ? { 'Authorization': `Bearer ${token}` } : {};

      // Fetch all rides
      const ridesRes = await axios.get(`${API_URL}/all-rides`, { headers });
      const rides = ridesRes.data.rides || [];
      const completedRides = rides.filter(r => r.status === 'completed');

      // Calculate totals
      const total = completedRides.reduce((sum, r) => sum + (r.total_fare || 0), 0);
      const commission = total * 0.08;

      // Today
      const today = new Date().toISOString().split('T')[0];
      const todayRides = completedRides.filter(r => r.created_at?.startsWith(today));
      const todayRevenue = todayRides.reduce((sum, r) => sum + (r.total_fare || 0), 0);

      // This week
      const weekAgo = new Date();
      weekAgo.setDate(weekAgo.getDate() - 7);
      const weekRides = completedRides.filter(r => new Date(r.created_at) >= weekAgo);
      const weekRevenue = weekRides.reduce((sum, r) => sum + (r.total_fare || 0), 0);

      // This month
      const monthAgo = new Date();
      monthAgo.setDate(monthAgo.getDate() - 30);
      const monthRides = completedRides.filter(r => new Date(r.created_at) >= monthAgo);
      const monthRevenue = monthRides.reduce((sum, r) => sum + (r.total_fare || 0), 0);

      // By vehicle
      const vehicleData = {};
      completedRides.forEach(r => {
        const type = r.vehicle_type || 'boda';
        vehicleData[type] = (vehicleData[type] || 0) + (r.total_fare || 0);
      });

      // Weekly trend (last 7 days)
      const weekTrend = [];
      for (let i = 6; i >= 0; i--) {
        const date = new Date();
        date.setDate(date.getDate() - i);
        const dateStr = date.toISOString().split('T')[0];
        const dayRides = completedRides.filter(r => r.created_at?.startsWith(dateStr));
        const revenue = dayRides.reduce((sum, r) => sum + (r.total_fare || 0), 0);
        const count = dayRides.length;
        weekTrend.push({ 
          date: dateStr, 
          revenue, 
          rides: count,
          avg: count > 0 ? revenue / count : 0
        });
      }

      // Daily trend (last 30 days, sampled)
      const dailyData = [];
      for (let i = 29; i >= 0; i--) {
        const date = new Date();
        date.setDate(date.getDate() - i);
        const dateStr = date.toISOString().split('T')[0];
        const dayRides = completedRides.filter(r => r.created_at?.startsWith(dateStr));
        const revenue = dayRides.reduce((sum, r) => sum + (r.total_fare || 0), 0);
        if (i % 2 === 0 || revenue > 0) {
          dailyData.push({ date: dateStr, revenue });
        }
      }

      const avgDaily = completedRides.length > 0 ? total / completedRides.length : 0;

      // Recent transactions
      const recent = completedRides.slice(0, 20).map(r => ({
        id: r.id,
        rider: r.customer_name || 'Rider',
        driver: r.driver_name || 'Driver',
        amount: r.total_fare || 0,
        commission: (r.total_fare || 0) * 0.08,
        date: r.created_at,
        status: r.status,
        vehicle: r.vehicle_type || 'boda',
      }));

      setEarnings({
        total,
        commission,
        thisMonth: monthRevenue,
        thisWeek: weekRevenue,
        today: todayRevenue,
        pendingPayouts: commission * 0.5,
        totalWithdrawn: commission * 0.3,
        lastPayout: new Date(Date.now() - 86400000).toISOString(),
        averageDaily: avgDaily,
      });

      setTransactions(recent);
      setWeeklyTrend(weekTrend);
      setDailyTrend(dailyData);
      setVehicleEarnings(Object.entries(vehicleData).map(([type, amount]) => ({ 
        type, 
        amount 
      })));

    } catch (error) {
      console.error('Error fetching earnings:', error);
      setError(error.response?.data?.error || 'Failed to load earnings data');
    } finally {
      setLoading(false);
    }
  };

  const StatCard = ({ title, value, icon, color, subtitle }) => (
    <div style={{
      backgroundColor: 'white',
      borderRadius: '12px',
      padding: '20px',
      boxShadow: '0 1px 3px rgba(0,0,0,0.1)',
      flex: 1,
      minWidth: '160px',
      transition: 'transform 0.2s',
      cursor: 'pointer',
    }}
    onMouseEnter={(e) => e.currentTarget.style.transform = 'translateY(-2px)'}
    onMouseLeave={(e) => e.currentTarget.style.transform = 'translateY(0)'}
    >
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <div>
          <p style={{ color: '#64748b', fontSize: '13px', margin: 0 }}>{title}</p>
          <p style={{ fontSize: '24px', fontWeight: 'bold', margin: '8px 0 0 0' }}>
            {loading ? '...' : value}
          </p>
          {subtitle && (
            <p style={{ fontSize: '11px', color: '#94a3b8', margin: '4px 0 0 0' }}>{subtitle}</p>
          )}
        </div>
        <div style={{
          backgroundColor: color,
          borderRadius: '50%',
          width: '44px',
          height: '44px',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
        }}>
          <span style={{ fontSize: '20px' }}>{icon}</span>
        </div>
      </div>
    </div>
  );

  const getVehicleLabel = (type) => {
    const labels = {
      boda: '🛵 Boda',
      boda_electric: '⚡ Boda E',
      car: '🚗 Car',
      car_xl: '🚙 Car XL',
    };
    return labels[type] || type;
  };

  const COLORS = ['#f97316', '#10b981', '#3b82f6', '#8b5cf6'];

  const getExportData = () => {
    return transactions.map(t => ({
      'Ride ID': t.id,
      Rider: t.rider,
      Driver: t.driver,
      Amount: t.amount,
      Commission: t.commission,
      Vehicle: t.vehicle,
      Date: new Date(t.date).toLocaleDateString(),
    }));
  };

  if (loading && transactions.length === 0) {
    return (
      <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100vh' }}>
        <div style={{ textAlign: 'center' }}>
          <div style={{ fontSize: '40px', marginBottom: '16px' }}>🔄</div>
          <div style={{ fontSize: '18px', color: '#64748b' }}>Loading earnings data...</div>
        </div>
      </div>
    );
  }

  return (
    <div style={{ padding: '0 0 20px 0' }}>
      {/* Header */}
      <div style={{ 
        display: 'flex', 
        justifyContent: 'space-between', 
        alignItems: 'center', 
        marginBottom: '24px',
        flexWrap: 'wrap',
        gap: '12px',
      }}>
        <div>
          <h1 style={{ margin: 0, fontSize: '24px', fontWeight: 'bold' }}>💰 Earnings Reports</h1>
          <div style={{ display: 'flex', gap: '16px', marginTop: '4px', fontSize: '13px', color: '#64748b', flexWrap: 'wrap' }}>
            <span>Total Revenue: UGX {earnings.total.toLocaleString()}</span>
            <span style={{ color: '#10b981' }}>📈 Commission: UGX {earnings.commission.toLocaleString()}</span>
            {error && <span style={{ color: '#ef4444' }}>⚠️ {error}</span>}
          </div>
        </div>
        <div style={{ display: 'flex', gap: '10px', flexWrap: 'wrap', alignItems: 'center' }}>
          <select
            value={timeRange}
            onChange={(e) => setTimeRange(e.target.value)}
            style={{
              padding: '8px 16px',
              borderRadius: '8px',
              border: '1px solid #e2e8f0',
              backgroundColor: 'white',
              fontSize: '14px',
              cursor: 'pointer',
            }}
          >
            <option value="week">Last 7 Days</option>
            <option value="month">Last 30 Days</option>
            <option value="quarter">Last 90 Days</option>
          </select>
          <ExportButton 
            data={getExportData()} 
            filename="earnings_export" 
            label="Export CSV"
          />
          <button
            onClick={fetchEarnings}
            style={{
              padding: '10px 20px',
              backgroundColor: '#f97316',
              color: 'white',
              border: 'none',
              borderRadius: '10px',
              cursor: 'pointer',
              fontSize: '14px',
              fontWeight: '500',
            }}
          >
            🔄 Refresh
          </button>
        </div>
      </div>

      {error && (
        <div style={{
          backgroundColor: '#fee2e2',
          color: '#991b1b',
          padding: '14px 18px',
          borderRadius: '10px',
          marginBottom: '16px',
          border: '1px solid #fecaca',
        }}>
          ⚠️ {error}
        </div>
      )}

      {/* Stats Cards */}
      <div style={{
        display: 'grid',
        gridTemplateColumns: 'repeat(auto-fit, minmax(160px, 1fr))',
        gap: '16px',
        marginBottom: '24px',
      }}>
        <StatCard 
          title="Total Revenue" 
          value={`UGX ${earnings.total.toLocaleString()}`} 
          icon="💰" 
          color="#fce7f3" 
          subtitle={`${earnings.totalRides || 0} rides`}
        />
        <StatCard 
          title="Today" 
          value={`UGX ${earnings.today.toLocaleString()}`} 
          icon="📅" 
          color="#dbeafe" 
        />
        <StatCard 
          title="This Week" 
          value={`UGX ${earnings.thisWeek.toLocaleString()}`} 
          icon="📊" 
          color="#dcfce7" 
        />
        <StatCard 
          title="This Month" 
          value={`UGX ${earnings.thisMonth.toLocaleString()}`} 
          icon="📈" 
          color="#fef3c7" 
        />
        <StatCard 
          title="Commission (8%)" 
          value={`UGX ${earnings.commission.toLocaleString()}`} 
          icon="💸" 
          color="#fee2e2" 
        />
        <StatCard 
          title="Pending Payouts" 
          value={`UGX ${earnings.pendingPayouts.toLocaleString()}`} 
          icon="⏳" 
          color="#fef3c7" 
        />
        <StatCard 
          title="Total Withdrawn" 
          value={`UGX ${earnings.totalWithdrawn.toLocaleString()}`} 
          icon="🏦" 
          color="#e0e7ff" 
        />
        <StatCard 
          title="Avg Daily" 
          value={`UGX ${earnings.averageDaily.toFixed(0)}`} 
          icon="📊" 
          color="#f1f5f9" 
        />
      </div>

      {/* Charts */}
      <div style={{
        display: 'grid',
        gridTemplateColumns: '1fr 1fr',
        gap: '24px',
        marginBottom: '24px',
      }}>
        {/* Weekly Trend */}
        <div style={{
          backgroundColor: 'white',
          borderRadius: '12px',
          padding: '20px',
          boxShadow: '0 1px 3px rgba(0,0,0,0.1)',
          height: '300px',
        }}>
          <h3 style={{ marginTop: 0, fontSize: '16px' }}>Weekly Revenue Trend</h3>
          <ResponsiveContainer width="100%" height="85%">
            <BarChart data={weeklyTrend}>
              <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" />
              <XAxis dataKey="date" tick={{ fontSize: 11 }} />
              <YAxis tick={{ fontSize: 11 }} />
              <Tooltip />
              <Legend />
              <Bar dataKey="revenue" fill="#f97316" name="Revenue" />
              <Bar dataKey="rides" fill="#3b82f6" name="Rides" />
            </BarChart>
          </ResponsiveContainer>
        </div>

        {/* Vehicle Earnings */}
        <div style={{
          backgroundColor: 'white',
          borderRadius: '12px',
          padding: '20px',
          boxShadow: '0 1px 3px rgba(0,0,0,0.1)',
          height: '300px',
        }}>
          <h3 style={{ marginTop: 0, fontSize: '16px' }}>Revenue by Vehicle</h3>
          {vehicleEarnings.length === 0 ? (
            <div style={{ textAlign: 'center', padding: '40px', color: '#94a3b8' }}>
              No data available
            </div>
          ) : (
            <ResponsiveContainer width="100%" height="85%">
              <PieChart>
                <Pie
                  data={vehicleEarnings}
                  cx="50%"
                  cy="50%"
                  innerRadius={60}
                  outerRadius={90}
                  paddingAngle={5}
                  dataKey="amount"
                  label={({ type, percent }) => `${getVehicleLabel(type)} ${(percent * 100).toFixed(0)}%`}
                  labelLine={{ stroke: '#94a3b8' }}
                >
                  {vehicleEarnings.map((entry, index) => (
                    <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />
                  ))}
                </Pie>
                <Tooltip formatter={(value) => `UGX ${value.toLocaleString()}`} />
                <Legend />
              </PieChart>
            </ResponsiveContainer>
          )}
        </div>
      </div>

      {/* Daily Trend Chart */}
      <div style={{
        backgroundColor: 'white',
        borderRadius: '12px',
        padding: '20px',
        marginBottom: '24px',
        boxShadow: '0 1px 3px rgba(0,0,0,0.1)',
        height: '250px',
      }}>
        <h3 style={{ marginTop: 0, fontSize: '16px' }}>Daily Revenue Trend (Last 30 Days)</h3>
        <ResponsiveContainer width="100%" height="85%">
          <LineChart data={dailyTrend}>
            <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" />
            <XAxis dataKey="date" tick={{ fontSize: 10 }} interval={2} />
            <YAxis tick={{ fontSize: 11 }} />
            <Tooltip formatter={(value) => `UGX ${value.toLocaleString()}`} />
            <Line type="monotone" dataKey="revenue" stroke="#10b981" strokeWidth={2} dot={false} />
          </LineChart>
        </ResponsiveContainer>
      </div>

      {/* Transactions Table */}
      <div style={{
        backgroundColor: 'white',
        borderRadius: '12px',
        padding: '20px',
        boxShadow: '0 1px 3px rgba(0,0,0,0.1)',
        overflow: 'auto',
      }}>
        <h3 style={{ marginTop: 0, fontSize: '16px' }}>Recent Transactions</h3>
        {transactions.length === 0 ? (
          <div style={{ textAlign: 'center', padding: '20px', color: '#94a3b8' }}>
            No transactions yet
          </div>
        ) : (
          <table style={{ width: '100%', borderCollapse: 'collapse' }}>
            <thead>
              <tr style={{ borderBottom: '1px solid #e2e8f0', backgroundColor: '#f8fafc' }}>
                <th style={{ textAlign: 'left', padding: '12px', fontWeight: '600', fontSize: '12px' }}>Ride ID</th>
                <th style={{ textAlign: 'left', padding: '12px', fontWeight: '600', fontSize: '12px' }}>Rider</th>
                <th style={{ textAlign: 'left', padding: '12px', fontWeight: '600', fontSize: '12px' }}>Driver</th>
                <th style={{ textAlign: 'left', padding: '12px', fontWeight: '600', fontSize: '12px' }}>Vehicle</th>
                <th style={{ textAlign: 'right', padding: '12px', fontWeight: '600', fontSize: '12px' }}>Amount</th>
                <th style={{ textAlign: 'right', padding: '12px', fontWeight: '600', fontSize: '12px' }}>Commission</th>
                <th style={{ textAlign: 'right', padding: '12px', fontWeight: '600', fontSize: '12px' }}>Date</th>
              </tr>
            </thead>
            <tbody>
              {transactions.map((t) => (
                <tr key={t.id} style={{ borderBottom: '1px solid #f1f5f9' }}>
                  <td style={{ padding: '12px', fontWeight: '500' }}>#{t.id}</td>
                  <td style={{ padding: '12px' }}>{t.rider}</td>
                  <td style={{ padding: '12px' }}>{t.driver}</td>
                  <td style={{ padding: '12px' }}>{getVehicleLabel(t.vehicle)}</td>
                  <td style={{ padding: '12px', textAlign: 'right', fontWeight: 'bold', color: '#10b981' }}>
                    UGX {t.amount.toLocaleString()}
                  </td>
                  <td style={{ padding: '12px', textAlign: 'right', color: '#f97316' }}>
                    UGX {t.commission.toFixed(0).toLocaleString()}
                  </td>
                  <td style={{ padding: '12px', textAlign: 'right', fontSize: '12px', color: '#64748b' }}>
                    {new Date(t.date).toLocaleDateString()}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
        <div style={{ padding: '12px', color: '#64748b', fontSize: '13px', borderTop: '1px solid #e2e8f0', marginTop: '12px' }}>
          Showing {transactions.length} recent transactions
        </div>
      </div>

      {/* Payout Management */}
      <div style={{
        backgroundColor: 'white',
        borderRadius: '12px',
        padding: '20px',
        marginTop: '24px',
        boxShadow: '0 1px 3px rgba(0,0,0,0.1)',
      }}>
        <h3 style={{ marginTop: 0, fontSize: '16px' }}>💳 Payout Management</h3>
        <div style={{ display: 'flex', gap: '24px', flexWrap: 'wrap', alignItems: 'center' }}>
          <div>
            <p style={{ fontSize: '12px', color: '#64748b', margin: 0 }}>Available for Payout</p>
            <p style={{ fontSize: '20px', fontWeight: 'bold', color: '#10b981' }}>
              UGX {earnings.pendingPayouts.toLocaleString()}
            </p>
          </div>
          <div>
            <p style={{ fontSize: '12px', color: '#64748b', margin: 0 }}>Last Payout</p>
            <p style={{ fontSize: '14px', fontWeight: '500' }}>
              {earnings.lastPayout ? new Date(earnings.lastPayout).toLocaleDateString() : 'Never'}
            </p>
          </div>
          <button
            style={{
              padding: '10px 24px',
              backgroundColor: '#f97316',
              color: 'white',
              border: 'none',
              borderRadius: '8px',
              cursor: 'pointer',
              fontWeight: '500',
            }}
            onClick={() => alert('Payout request initiated')}
          >
            💳 Request Payout
          </button>
          <button
            style={{
              padding: '10px 24px',
              backgroundColor: '#e2e8f0',
              color: '#475569',
              border: 'none',
              borderRadius: '8px',
              cursor: 'pointer',
            }}
            onClick={() => alert('View payout history')}
          >
            📊 History
          </button>
        </div>
      </div>
    </div>
  );
}

export default Earnings;